/**
 * Authentication Store
 *
 * Zustand store for managing authentication state.
 * Handles login, logout, and token management.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import api, { setTokens, clearTokens, getAccessToken } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'
import { useTenantStore } from './tenantStore'

// User type
export interface User {
  id: number
  username: string
  email: string | null
  full_name: string
  role: 'admin' | 'manager' | 'seller' | 'cashier' | 'accountant'
  is_active: boolean
  avatar?: string | null
}

// Auth state
interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean
  error: string | null

  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  refreshUser: () => Promise<void>
  clearError: () => void
}

/**
 * Create auth store with persistence
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,

      /**
       * Login with username and password
       */
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await api.post('/auth/login', {
            email,
            password,
          })

          const { access_token, refresh_token, user } = response.data

          // Store tokens
          setTokens(access_token, refresh_token)

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })

          // Fetch tenant data after successful login
          try {
            await useTenantStore.getState().fetchTenant()
          } catch {
            // Tenant fetch failure shouldn't block login
          }
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : 'Error de autenticación. Verifica tus credenciales.'

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          })

          throw error
        }
      },

      /**
       * Logout and clear tokens
       */
      logout: async () => {
        try {
          await api.post('/auth/logout')
        } catch {
          // Ignore logout errors
        } finally {
          clearTokens()
          // Clear tenant data
          useTenantStore.getState().clearTenant()
          // Clear React Query cache to prevent data leakage between tenants
          queryClient.clear()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      /**
       * Check current authentication status
       */
      checkAuth: async () => {
        // Prevent multiple simultaneous checks
        if (get().isInitialized || get().isLoading) {
          return
        }

        const token = getAccessToken()

        if (!token) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          })
          return
        }

        set({ isLoading: true })

        try {
          const response = await api.get('/users/me')

          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          })

          // Also fetch tenant data if authenticated
          try {
            await useTenantStore.getState().fetchTenant()
          } catch {
            // Tenant fetch failure shouldn't affect auth check
          }
        } catch {
          clearTokens()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          })
        }
      },

      /**
       * Refresh user data from server
       */
      refreshUser: async () => {
        try {
          const response = await api.get('/users/me')
          set({ user: response.data })
        } catch {
          // Ignore errors, user data will remain as is
        }
      },

      /**
       * Clear any authentication error
       */
      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
